import { CheckboxValueType } from 'antd/lib/checkbox/Group';
import { isEmpty, orderBy } from 'lodash';
import moment, { Moment } from 'moment';
import { DateConstant, FilterBarConstant } from 'src/constants';
import { GetOfferOrderListQueryParamsDto, GetOrderListQueryParamsDto } from 'src/dtos';
import {
  mapOfferOrderStatusLabelToStatusQueryParamDto,
  mapOrderStatusLabelToOrderStatusIdQueryParamDto,
  mapTransferStatusLabelToStatesQueryParamDto,
} from 'src/mappers';
import { OfferOrderStatusLabel, OrderStatusLabel, TransferStatusLabel } from 'src/models';

import { Maybe } from './types.utils';

export const onFixedTimelineFilterSelected = (value: FilterBarConstant.FixedTimeline) => {
  let startDate: Moment;
  let endDate: Moment;

  switch (value) {
    case FilterBarConstant.FixedTimeline.ThisYear:
      startDate = moment().startOf('year');
      endDate = moment().add(1, 'day');
      break;
    case FilterBarConstant.FixedTimeline.ThisMonth:
      startDate = moment().startOf('month');
      endDate = moment().add(1, 'day');
      break;
    case FilterBarConstant.FixedTimeline.ThisWeek:
      startDate = moment().startOf('week');
      endDate = moment().add(1, 'day');
      break;
    default:
      startDate = moment().startOf('year');
      endDate = moment().add(1, 'day');
      break;
  }

  return { startDate, endDate };
};

const mapFilterByToTransferState = (filterBy: CheckboxValueType[] = []): Maybe<string> => {
  const statusList: TransferStatusLabel[] = filterBy.reduce((all, current) => {
    const filterValue = current.toString();
    const [value, category] = filterValue.split('_');

    if (category === FilterBarConstant.StatusCategory.MoneyTransfer) {
      all.push(value as TransferStatusLabel);
    }

    return all;
  }, [] as TransferStatusLabel[]);

  if (isEmpty(statusList)) {
    return;
  }

  const transferStateList = statusList.map(mapTransferStatusLabelToStatesQueryParamDto);

  return transferStateList.join(',');
};

export const mapFilterToGetTransferListQueryParamsDto = (filters: FilterBarConstant.Sample) => {
  const defaultTimeline = onFixedTimelineFilterSelected(FilterBarConstant.FixedTimeline.ThisYear);
  let states: Maybe<string>;
  let { startDate, endDate, sortBy } = {
    startDate: defaultTimeline.startDate.format(DateConstant.ISO_1806_FORMAT),
    endDate: defaultTimeline.endDate.format(DateConstant.ISO_1806_FORMAT),
    sortBy: filters.sortBy === FilterBarConstant.SortBy.Newest ? 'descending' : 'ascending',
  };

  if (filters.timeline !== undefined) {
    const timelineDate = onFixedTimelineFilterSelected(filters.timeline);
    startDate = timelineDate.startDate.format(DateConstant.ISO_1806_FORMAT);
    endDate = timelineDate.endDate.format(DateConstant.ISO_1806_FORMAT);
  }

  if (filters.customTimeline !== undefined) {
    const [customStartDate, customEndDate] = filters.customTimeline;
    startDate = customStartDate.format(DateConstant.ISO_1806_FORMAT);
    endDate = customEndDate.format(DateConstant.ISO_1806_FORMAT);
  }

  if (!isEmpty(filters.filterBy)) {
    states = mapFilterByToTransferState(filters.filterBy);
  }

  return {
    startDate,
    endDate,
    states,
    sortBy,
  };
};

const mapFilterByToOrderStatusId = (filterBy: CheckboxValueType[] = []): Maybe<string> => {
  const statusList: OrderStatusLabel[] = filterBy.reduce((all, current) => {
    const filterValue = current.toString();
    const [value, category] = filterValue.split('_');

    if (category === FilterBarConstant.StatusCategory.Order) {
      all.push(value as OrderStatusLabel);
    }

    return all;
  }, [] as OrderStatusLabel[]);

  if (isEmpty(statusList)) {
    return;
  }

  const orderStatusIdList = statusList.map(status => mapOrderStatusLabelToOrderStatusIdQueryParamDto(status));

  return orderStatusIdList.join(',');
};

const mapFilterByToOfferOrderStatus = (filterBy: CheckboxValueType[] = []): Maybe<string> => {
  const statusList: OfferOrderStatusLabel[] = filterBy.reduce((all, current) => {
    const filterValue = current.toString();
    const [value, category] = filterValue.split('_');

    if (category === FilterBarConstant.StatusCategory.OfferOrder) {
      all.push(value as OfferOrderStatusLabel);
    }

    return all;
  }, [] as OfferOrderStatusLabel[]);

  if (isEmpty(statusList)) {
    return;
  }

  const offerOrderStatusList = statusList.map(status => mapOfferOrderStatusLabelToStatusQueryParamDto(status));

  return offerOrderStatusList.join(',');
};

export const mapFilterToGetOrderListQueryParamsDto = (
  filters: FilterBarConstant.Sample,
  { pageIndex, pageSize, skipFilterBy }: { pageIndex?: number; pageSize?: number; skipFilterBy?: boolean } = {
    pageIndex: 0,
    pageSize: 50,
  },
): GetOrderListQueryParamsDto => {
  const defaultTimeline = onFixedTimelineFilterSelected(FilterBarConstant.FixedTimeline.ThisYear);
  let orderStatusId;
  let { startDate, endDate } = {
    startDate: defaultTimeline.startDate.format(DateConstant.ISO_1806_FORMAT),
    endDate: defaultTimeline.endDate.format(DateConstant.ISO_1806_FORMAT),
  };

  if (filters.timeline !== undefined) {
    const timelineDate = onFixedTimelineFilterSelected(filters.timeline);
    startDate = timelineDate.startDate.format(DateConstant.ISO_1806_FORMAT);
    endDate = timelineDate.endDate.format(DateConstant.ISO_1806_FORMAT);
  }

  if (filters.customTimeline !== undefined) {
    const [customStartDate, customEndDate] = filters.customTimeline;
    startDate = customStartDate.format(DateConstant.ISO_1806_FORMAT);
    endDate = customEndDate.format(DateConstant.ISO_1806_FORMAT);
  }

  if (!isEmpty(filters.filterBy) && !skipFilterBy) {
    const statusList = mapFilterByToOrderStatusId(filters.filterBy);
    const orderedStatusList = orderBy(statusList?.split(','), [n => Number(n)], ['asc']);
    orderStatusId = orderedStatusList?.join(',') ?? undefined;
  }

  return {
    dateFrom: startDate,
    dateTo: endDate,
    orderStatusId,
    pageIndex,
    pageSize,
  };
};

export const mapFilterToGetOfferOrderListQueryParamsDto = (
  filters: FilterBarConstant.Sample,
  { take, skip }: { take?: number; skip?: number } = { take: 50, skip: 0 },
): GetOfferOrderListQueryParamsDto => {
  const defaultTimeline = onFixedTimelineFilterSelected(FilterBarConstant.FixedTimeline.ThisYear);
  let status: Maybe<string>;
  let { startDate, endDate } = {
    startDate: defaultTimeline.startDate.format(DateConstant.ISO_1806_FORMAT),
    endDate: defaultTimeline.endDate.format(DateConstant.ISO_1806_FORMAT),
  };

  if (filters.timeline !== undefined) {
    const timelineDate = onFixedTimelineFilterSelected(filters.timeline);
    startDate = timelineDate.startDate.format(DateConstant.ISO_1806_FORMAT);
    endDate = timelineDate.endDate.format(DateConstant.ISO_1806_FORMAT);
  }

  if (filters.customTimeline !== undefined) {
    const [customStartDate, customEndDate] = filters.customTimeline;
    startDate = customStartDate.format(DateConstant.ISO_1806_FORMAT);
    endDate = customEndDate.format(DateConstant.ISO_1806_FORMAT);
  }

  if (!isEmpty(filters.filterBy)) {
    status = mapFilterByToOfferOrderStatus(filters.filterBy);
  }

  return {
    startDate,
    endDate,
    status,
    take,
    skip,
  };
};
